/**
 * Collection Manager Modal
 *
 * Provides a modal interface for browsing and selecting collections.
 * Follows the same pattern as agent-manager.js.
 */

import { state } from './state.js';
import { escapeHtml } from './html-escape.js';
import { getHeaders } from './api.js';

let collections = [];
let selectedCollectionName = null;
let modalOpen = false;

const modalElements = {
    overlay: null,
    panel: null,
    closeBtn: null,
    searchInput: null,
    collectionList: null,
    clearBtn: null,
    selectBtn: null
};

// Callback for when collection is selected
let onCollectionSelected = null;

/**
 * Initialize the Collection Manager
 * @param {Function} callback - Called with (collectionName, collectionData) when collection is selected
 */
export function initCollectionManager(callback) {
    onCollectionSelected = callback;
    createModalHTML();
    cacheElements();
    setupEventListeners();
}

/**
 * Create the modal HTML structure
 */
function createModalHTML() {
    const modalHTML = `
        <div id="collection-manager-overlay" class="modal-overlay"></div>
        <div id="collection-manager-panel" class="context-manager-panel">
            <div class="context-manager-header">
                <h2>Select Collection</h2>
                <button id="collection-manager-close" class="modal-close">&times;</button>
            </div>
            <div class="context-manager-search">
                <input type="text" id="collection-search" placeholder="Search collections...">
            </div>
            <div id="collection-list" class="context-manager-list">
                <div class="context-manager-empty">Loading collections...</div>
            </div>
            <div class="context-manager-footer">
                <button id="collection-clear-btn" class="btn-secondary">Clear</button>
                <button id="collection-select-btn" class="menu-btn primary" disabled>Select Collection</button>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', modalHTML);
}

/**
 * Cache DOM elements
 */
function cacheElements() {
    modalElements.overlay = document.getElementById('collection-manager-overlay');
    modalElements.panel = document.getElementById('collection-manager-panel');
    modalElements.closeBtn = document.getElementById('collection-manager-close');
    modalElements.searchInput = document.getElementById('collection-search');
    modalElements.collectionList = document.getElementById('collection-list');
    modalElements.clearBtn = document.getElementById('collection-clear-btn');
    modalElements.selectBtn = document.getElementById('collection-select-btn');
}

/**
 * Setup event listeners
 */
function setupEventListeners() {
    modalElements.overlay?.addEventListener('click', closeCollectionManager);
    modalElements.closeBtn?.addEventListener('click', closeCollectionManager);
    modalElements.searchInput?.addEventListener('input', handleSearch);
    modalElements.clearBtn?.addEventListener('click', handleClear);
    modalElements.selectBtn?.addEventListener('click', handleSelect);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modalOpen) {
            closeCollectionManager();
        }
    });
}

/**
 * Open the Collection Manager modal
 */
export async function openCollectionManager() {
    modalOpen = true;
    selectedCollectionName = null;

    // Reset search
    if (modalElements.searchInput) {
        modalElements.searchInput.value = '';
    }

    // Show modal
    modalElements.overlay?.classList.add('active');
    modalElements.panel?.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Fetch and render collections
    await fetchCollections();
    renderCollectionList();

    // Update button states
    updateButtonStates();
}

/**
 * Close the Collection Manager modal
 */
export function closeCollectionManager() {
    modalOpen = false;
    modalElements.overlay?.classList.remove('active');
    modalElements.panel?.classList.remove('open');
    document.body.style.overflow = '';
}

/**
 * Fetch collections from API
 */
async function fetchCollections() {
    try {
        const response = await fetch('/collections', { headers: getHeaders(false) });
        if (!response.ok) throw new Error('Failed to fetch collections');
        const data = await response.json();
        collections = data.collections || [];
    } catch (error) {
        console.error('Error fetching collections:', error);
        collections = [];
    }
}

/**
 * Render the collection list
 */
function renderCollectionList() {
    if (!modalElements.collectionList) return;

    const searchTerm = modalElements.searchInput?.value.toLowerCase() || '';

    // Filter collections by search term
    const filteredCollections = collections.filter(collection => {
        const name = collection.name?.toLowerCase() || '';
        const title = collection.title?.toLowerCase() || '';
        const description = collection.description?.toLowerCase() || '';
        return name.includes(searchTerm) || title.includes(searchTerm) || description.includes(searchTerm);
    });

    if (filteredCollections.length === 0) {
        modalElements.collectionList.innerHTML = `
            <div class="context-manager-empty">
                ${collections.length === 0 ? 'No collections available' : 'No collections match your search'}
            </div>
        `;
        return;
    }

    // Sort alphabetically by display name
    filteredCollections.sort((a, b) => {
        const nameA = a.title || a.name;
        const nameB = b.title || b.name;
        return nameA.localeCompare(nameB);
    });

    let html = '<div class="context-card-grid">';
    for (const collection of filteredCollections) {
        const isSelected = collection.name === selectedCollectionName;
        const isCurrent = collection.name === state.loadedCollection;

        // Use title if available, otherwise format name
        const displayName = collection.title || collection.name
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

        html += `
            <div class="context-card ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}"
                 data-collection-name="${escapeHtml(collection.name)}"
                 data-collection-title="${escapeHtml(collection.title || '')}">
                <div class="context-card-header">
                    <span class="context-card-name">${escapeHtml(displayName)}</span>
                    <div class="context-card-badges">
                        ${isCurrent ? '<span class="badge current">LOADED</span>' : ''}
                    </div>
                </div>
                ${collection.description ? `<div class="context-card-description">${escapeHtml(collection.description)}</div>` : ''}
                ${renderCollectionChips(collection)}
            </div>
        `;
    }
    html += '</div>';

    modalElements.collectionList.innerHTML = html;

    // Add click handlers to cards
    modalElements.collectionList.querySelectorAll('.context-card').forEach(card => {
        card.addEventListener('click', () => {
            const collectionName = card.dataset.collectionName;
            selectCollection(collectionName);
        });
    });
}

/**
 * Select a collection (highlight in list)
 */
function selectCollection(collectionName) {
    selectedCollectionName = collectionName;

    // Update visual selection
    modalElements.collectionList?.querySelectorAll('.context-card').forEach(card => {
        card.classList.toggle('selected', card.dataset.collectionName === collectionName);
    });

    updateButtonStates();
}

/**
 * Handle search input
 */
function handleSearch() {
    renderCollectionList();
}

/**
 * Handle clear button click
 */
function handleClear() {
    selectedCollectionName = null;
    if (onCollectionSelected) {
        onCollectionSelected(null, null);
    }
    closeCollectionManager();
}

/**
 * Handle select button click
 */
function handleSelect() {
    if (!selectedCollectionName) return;

    const collection = collections.find(c => c.name === selectedCollectionName);
    if (onCollectionSelected) {
        onCollectionSelected(selectedCollectionName, collection);
    }
    closeCollectionManager();
}

/**
 * Update button states
 */
function updateButtonStates() {
    if (modalElements.selectBtn) {
        modalElements.selectBtn.disabled = !selectedCollectionName;
    }
    if (modalElements.clearBtn) {
        // Enable clear if there's a currently loaded or selected collection
        modalElements.clearBtn.disabled = !state.loadedCollection && !state.currentCollection;
    }
}

function renderCollectionChips(collection) {
    const chips = [];
    const kbs = collection.kbs || [];

    for (const kb of kbs) {
        const name = kb.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        chips.push(`<span class="context-card-chip kb" title="${escapeHtml(kb)}">${escapeHtml(name)}</span>`);
    }
    return chips.length ? `<div class="context-card-chips">${chips.join('')}</div>` : '';
}

/**
 * Escape HTML to prevent XSS
 */

